import { NavLink } from 'react-router-dom'
import { LogOut, X } from 'lucide-react'

import { useAuth } from '@/auth/AuthContext'
import { ROLE_LABELS } from '@/auth/permissions'
import { Avatar } from '@/components/ui/Avatar'
import { IconButton } from '@/components/ui/Button'
import { useEnabledModules } from '@/hooks/useModules'
import { cn } from '@/lib/cn'
import { visibleNavItems } from './navigation'
import logoUrl from '/legato-logo.jpg'

interface SideNavProps {
  /** Na mobile panel wysuwa się z lewej; na desktopie jest zawsze widoczny. */
  isOpen: boolean
  onClose: () => void
  onLogout: () => void
}

export function SideNav({ isOpen, onClose, onLogout }: SideNavProps) {
  const { user } = useAuth()
  const enabledModules = useEnabledModules()
  const items = visibleNavItems(user, enabledModules)

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-secondary/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-dvh w-72 flex-col bg-surface px-4 py-6',
          'border-r border-outline/10 transition-transform duration-200 lg:w-64 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Nawigacja główna"
      >
        <div className="mb-8 flex items-center gap-3 px-2">
          <img
            src={logoUrl}
            alt=""
            className="h-11 w-11 rounded-lg object-cover"
            width={44}
            height={44}
          />
          <div className="min-w-0 flex-1">
            <p className="font-headline text-xl font-bold leading-tight text-primary">Legato</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
              Szkoła Muzyki
            </p>
          </div>
          <IconButton
            label="Zamknij menu"
            icon={<X size={18} />}
            onClick={onClose}
            className="lg:hidden"
          />
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto">
          {items.map((item) => {
            const Icon = item.icon

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-4 py-3 transition-all duration-200',
                    isActive
                      ? 'border-r-4 border-primary bg-primary/5 font-bold text-primary'
                      : 'text-on-surface-variant hover:bg-surface-container-low hover:text-primary',
                  )
                }
              >
                <Icon size={20} aria-hidden="true" />
                <span className="text-sm font-semibold">{item.label}</span>
              </NavLink>
            )
          })}
        </nav>

        <div className="mt-auto space-y-3 border-t border-outline/10 pt-4">
          <div className="flex items-center gap-3 px-2">
            <Avatar name={user?.full_name ?? '?'} />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-on-surface">{user?.full_name}</p>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant">
                {user ? ROLE_LABELS[user.role] : ''}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-secondary px-4 py-3 text-sm font-bold text-on-secondary transition-all hover:bg-black active:scale-[0.98]"
          >
            <LogOut size={18} aria-hidden="true" />
            Wyloguj się
          </button>
        </div>
      </aside>
    </>
  )
}
